import React, { ChangeEvent, FormEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";
import ReactModal from "react-modal";

import withTitle, { IViewTitleProps } from "../withTitle";
import withProps from "../withProps";
import withAuth from "../withAuth";
import { V1, V2 } from "../../Api";
import { formatDateLocalMonth, formatName, formatDateLocal } from "../../Utilities";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";

import ConfirmationDialog from "../ConfirmationDialog";
import { IAuthProps, WellKnownGroups } from "../AuthContext";

interface IMetricsAssessmentsViewState {
    isLoading: boolean | string;
    isReleaseDialogOpen: boolean;
    isDelegationDialogOpen: boolean;
    isAnyNotFinalized: boolean;
    assessmentDates: any[];
    date: string;
    assessments: any[];
    managers: any[];
    managerId: string;
}

interface IMetricsAssessmentsViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps, IViewTitleProps {
    mode?: "unreleased";
}

class MetricsAssessmentsView extends React.Component<IMetricsAssessmentsViewProps, IMetricsAssessmentsViewState> {
    constructor(props: Readonly<IMetricsAssessmentsViewProps>) {
        super(props);

        const state: IMetricsAssessmentsViewState = {
            isLoading: true,
            isReleaseDialogOpen: false,
            isDelegationDialogOpen: false,
            isAnyNotFinalized: false,
            assessmentDates: [],
            date: "",
            assessments: [],
            managers: [],
            managerId: "",
        };

        if (this.props.location.state) {
            state.date = this.props.location.state.date || "";
            state.managerId = this.props.location.state.managerId || "";
        }

        this.state = state;
        this.handleElementChanged = this.handleElementChanged.bind(this);
        this.formElementChanged = this.formElementChanged.bind(this);
        this.goAssessment = this.goAssessment.bind(this);
        this.loadAssessments = this.loadAssessments.bind(this);
        this.toggleReleaseDialog = this.toggleReleaseDialog.bind(this);
        this.toggleDelegationDialog = this.toggleDelegationDialog.bind(this);
        this.releaseAll = this.releaseAll.bind(this);
        this.delegateToManager = this.delegateToManager.bind(this);
    }

    public componentDidMount() {
        if (this.props.mode !== "unreleased") {
            this.loadData();
        }
        this.loadAssessments();
    }

    public render() {
        const isUnreleased = this.props.mode === "unreleased";
        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="assess:read:all" condition={!isUnreleased}>
                            <Link to="/metrics/assessment/unreleased" className="text-with-icon"><i className="icon-table" /><span>Tallying...</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="assess:read:all" condition={isUnreleased}>
                            <button type="button" className="text-with-icon" onClick={this.toggleReleaseDialog}><i className="icon-check" /><span>Release All</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="assess:read:all" condition={!isUnreleased}>
                            <button type="button" className="text-with-icon" onClick={this.toggleDelegationDialog}><i className="icon-user" /><span>View Manager's Assessments</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <ConfirmationDialog isOpen={this.state.isReleaseDialogOpen} description="Do you really want to release all assessments?" onConfirm={this.releaseAll} onRequestClose={this.toggleReleaseDialog}>
                    {this.state.isAnyNotFinalized
                        ? <>
                            <br />
                            <div className="warning-box slim">
                                <b>Some are not yet finalized, and will not be released.</b><br />
                                Please ensure all needed assessments are finalized before releasing.
                            </div>
                        </>
                        : null}
                </ConfirmationDialog>
                <ReactModal
                    isOpen={this.state.isDelegationDialogOpen} onRequestClose={this.toggleDelegationDialog}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="View manager's assessments">
                    <form className="input-form" onSubmit={this.delegateToManager}>
                        <section className="info-block">
                            <header>
                                <h3>View a manager's assessments</h3>
                            </header>

                            {
                                (this.state.managers && this.state.managers.length === 0)
                                    ? <div className="spinner spinner-center lds-dual-ring" />
                                    : <>
                                        <p>Select the manager who's assessments you want to view</p>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-user">Manager</label>
                                                <select id="frm-user" name="managerId" value={this.state.managerId} onChange={this.formElementChanged} autoComplete="off_please_chrome">
                                                    <option value="">Yourself</option>
                                                    {this.state.managers.map(a => <option key={a.id} value={a.id}>{a.displayName}</option>)}
                                                </select>
                                            </div>
                                        </section>
                                    </>
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button type="submit" className="button push-right">View</button>
                                <button type="button" className="button" onClick={this.toggleDelegationDialog}>Close</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                {this.props.mode !== "unreleased"
                    ? <div className="filter-control-box">
                        <span className="spring" />
                        <select name="date" value={this.state.date} onChange={this.handleElementChanged}>
                            <option value="">Unfinalized</option>
                            {this.state.assessmentDates.map(a => <option key={a} value={a}>{formatDateLocalMonth(a)}</option>)}
                        </select>
                    </div>
                    : <p>
                        The following are all of the unreleased assessments
                    </p>}
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th className="col-smallest">Assessment By</th>
                            <th className="col-smallest">Last Updated</th>
                            {isUnreleased ? <th className="col-smallest col-center" title="Is finalized?"><i className="icon-check-circle-o" /></th> : null}
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Assessments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true
                            ? <tr><td colSpan={10}><div className="spinner lds-dual-ring" /></td></tr>
                            : typeof this.state.isLoading === "string"
                                ? <tr><td colSpan={10} className="error-text">{this.state.isLoading}</td></tr>
                                : null}
                        {this.state.assessments.map(a =>
                            <tr key={a.id || a.employeeId} className="clickable" onClick={() => this.goAssessment(a)}>
                                <td>{formatName(a.name)}</td>
                                <td className="inline" data-caption="Assessment by: ">{formatName(a.assessedBy?.name) || "-"}</td>
                                <td className="inline" data-caption="Last updated: ">{formatDateLocal(a.lastUpdated) || "-"}</td>
                                {isUnreleased ? <td className="col-center">{a.isFinalized ? <i className="icon-check-circle-o" title="Finalized" /> : <i className="icon-times red" title="Not finalized" />}</td> : null}
                            </tr>
                        )}
                    </tbody>
                </table>
                {!this.state.managerId
                    ? <p>
                        <small>Looking for an assessment you've finalized? Check by date using the dropdown in the top-right.</small>
                    </p>
                    : null}
            </div>
        );
    }

    private handleElementChanged(e: ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        } as any, () => this.loadAssessments());
    }

    private formElementChanged(e: ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private goAssessment(assessment: any) {
        if (this.props.mode === "unreleased" || assessment.isFinalized || this.state.managerId)
            this.props.history.push(`/metrics/assessment/${assessment.id}`);
        else
            this.props.history.push(`/metrics/assessment/employee/${assessment.employeeId}/${assessment.id || "new"}`);
    }

    private async loadData() {
        try {
            const parameters = [];
            if (this.state.managerId)
                parameters.push(`manager=${this.state.managerId}`);

            const previousAssessments = await V2.get<any[]>(parameters.length > 0 ? `metrics/assessments/months?${parameters.join("&")}` : "metrics/assessments/months");

            this.setState({
                assessmentDates: previousAssessments.map(a => a.substr(0, 10))
            });

            if (this.props.authContext.hasRight("assess:read:all")) {
                const managers = await V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Managers}`)

                for (const manager of managers) {
                    manager.displayName = formatName(manager.name);
                }

                this.setState({
                    managers: managers.sort((a, b) => a.displayName.localeCompare(b.displayName))
                });
            }
        }
        catch (e) {
            // TODO: Show error
        }
    }

    private async loadAssessments() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const endpoint = this.props.mode === "unreleased"
                ? "metrics/assessments/unreleased"
                : "metrics/assessments";

            const parameters = [];
            if (this.state.date)
                parameters.push(`date=${this.state.date}`);
            if (this.state.managerId)
                parameters.push(`manager=${this.state.managerId}`);

            const assessments = await V2.get<any[]>(parameters.length > 0 ? `${endpoint}?${parameters.join("&")}` : endpoint);

            this.setState({
                isLoading: false,
                isAnyNotFinalized: assessments.some(a => !a.isFinalized),

                assessments
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load assessments"
            });
        }
    }

    private saveSearchState() {
        const currentLocation = this.props.location;
        currentLocation.state = {
            date: this.state.date,
            managerId: this.state.managerId,
        };
        this.props.history.replace(currentLocation);
    }

    private toggleReleaseDialog() {
        const isReleaseDialogOpen = this.state.isReleaseDialogOpen;

        this.setState({
            isReleaseDialogOpen: !isReleaseDialogOpen
        });
    }

    private async releaseAll() {
        await V2.post("metrics/assessments/release");
        await this.loadAssessments();
    }

    private toggleDelegationDialog() {
        const isDelegationDialogOpen = this.state.isDelegationDialogOpen;

        this.setState({
            isDelegationDialogOpen: !isDelegationDialogOpen
        });
    }

    private delegateToManager(e: FormEvent) {
        e.preventDefault();

        const manager = this.state.managers.find(a => a.id === this.state.managerId);
        if (!this.state.managerId)
            this.props.viewTitleContext.changeTitle("Employee Assessments");
        else
            this.props.viewTitleContext.changeTitle(`${manager.displayName}'s Employee Assessments`);

        this.setState({
            isDelegationDialogOpen: false
        }, () => {
            this.loadData();
            this.loadAssessments();
        });
    }
}

export default withTitle("Employee Assessments", withAuth(MetricsAssessmentsView));
export const MetricsAssessmentsUnreleasedView = withTitle("Employee Assessment Tallying", withProps(withAuth(MetricsAssessmentsView), { mode: "unreleased" }));
